/**
 * Tonight's data plumbing on top of the shared feature hooks:
 *
 * - `useFeedHeads` — the "N new since you were here" poll. Two cheap
 *   first-page queries (`['tonight','head',…]`), seeded from the main infinite
 *   queries' first page so mounting costs no extra request, then refetched
 *   every 60 s — only while `document.visibilityState === 'visible'` (the
 *   interval function returns `false` otherwise, and a focus refetch resumes
 *   it). Polling one page instead of refetching the infinite queries means a
 *   poll never rewrites the pages the reader is scrolled into.
 * - `useSessionCutoff` — the newest friend-item time the reader has been
 *   shown, kept in memory for the browser session (a module variable, so it
 *   survives navigating away and back, and resets on reload).
 * - `useBecauseYouFinished` — the "Because you finished …" source: similar
 *   titles for the most recently completed title in the library.
 */

import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import type { CommunityFeedPage, Title } from '@shared/types';

import {
  fetchActivityPage,
  fetchFollowingPage,
  FRIENDS_ACTIVITY_KEY,
  FRIENDS_FOLLOWING_KEY,
  type FriendActivityPage,
} from '@/features/friends/api';
import { useLibrary } from '@/features/library/api';
import { useSimilar } from '@/features/show/api';

import type { BecauseSource } from './feed-model';

export const POLL_INTERVAL_MS = 60_000;

export const TONIGHT_HEAD_KEY = ['tonight', 'head'] as const;
const ACTIVITY_HEAD_KEY = [...TONIGHT_HEAD_KEY, 'activity'] as const;
const FOLLOWING_HEAD_KEY = [...TONIGHT_HEAD_KEY, 'following'] as const;

function visibleInterval(): number | false {
  if (typeof document === 'undefined') return POLL_INTERVAL_MS;
  return document.visibilityState === 'visible' ? POLL_INTERVAL_MS : false;
}

/**
 * Seed a head query from the main infinite query's first page (same fetch,
 * same data), stamped with the main query's update time so it is fresh.
 */
function useSeededHead<T>(
  headKey: readonly unknown[],
  mainKey: readonly unknown[],
  fetchFirst: () => Promise<T>,
  mainReady: boolean,
) {
  const qc = useQueryClient();
  const [seeded, setSeeded] = useState(() => qc.getQueryData(headKey) !== undefined);
  useEffect(() => {
    if (seeded || !mainReady) return;
    const main = qc.getQueryState<InfiniteData<T>>(mainKey);
    const first = main?.data?.pages[0];
    if (first !== undefined && qc.getQueryData(headKey) === undefined) {
      qc.setQueryData(headKey, first, { updatedAt: main?.dataUpdatedAt || Date.now() });
    }
    setSeeded(true);
  }, [qc, headKey, mainKey, mainReady, seeded]);

  return useQuery({
    queryKey: headKey,
    queryFn: fetchFirst,
    enabled: seeded,
    staleTime: POLL_INTERVAL_MS,
    refetchInterval: visibleInterval,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

export function useFeedHeads(activityReady: boolean, followingReady: boolean) {
  const activity = useSeededHead<FriendActivityPage>(
    ACTIVITY_HEAD_KEY,
    FRIENDS_ACTIVITY_KEY,
    () => fetchActivityPage(),
    activityReady,
  );
  const following = useSeededHead<CommunityFeedPage>(
    FOLLOWING_HEAD_KEY,
    FRIENDS_FOLLOWING_KEY,
    () => fetchFollowingPage(),
    followingReady,
  );
  return { activity: activity.data?.items ?? [], following: following.data?.items ?? [] };
}

// --- session cutoff ------------------------------------------------------------

let sessionCutoff: number | null = null;
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function setSessionCutoff(value: number | null) {
  sessionCutoff = value;
  for (const listener of listeners) listener();
}

/** Tests only: forget the in-memory "last seen" time between cases. */
export function resetTonightSession() {
  setSessionCutoff(null);
}

/**
 * `[cutoff, setCutoff]` — the newest friend-item time already shown (epoch
 * ms), or null before the first load of the session.
 */
export function useSessionCutoff() {
  const cutoff = useSyncExternalStore(subscribe, () => sessionCutoff, () => sessionCutoff);
  const set = useCallback((value: number) => {
    setSessionCutoff(Math.max(value, sessionCutoff ?? 0));
  }, []);
  return [cutoff, set] as const;
}

// --- because you finished ---------------------------------------------------------

/** Posters in the because card. */
export const BECAUSE_POSTERS = 5;

export function useBecauseYouFinished(): BecauseSource | null {
  const library = useLibrary();
  const items = library.data?.items;
  const source: Title | undefined = useMemo(
    () => items?.find((item) => item.status === 'completed')?.title,
    [items],
  );
  const similar = useSimilar(source?.id ?? '', Boolean(source), source?.type);
  return useMemo(() => {
    if (!source || !similar.data) return null;
    const owned = new Set((items ?? []).map((item) => item.title.id));
    const titles = similar.data
      .filter((title) => title.id !== source.id && !owned.has(title.id))
      .slice(0, BECAUSE_POSTERS);
    return titles.length > 0 ? { source, titles } : null;
  }, [source, similar.data, items]);
}
